import { Component, OnInit, AfterViewInit, OnDestroy } from '@angular/core';
import { BaseComponent } from '../base-component';
import { AuthService } from '../service/auth.service';
import { AddressService } from '../service/address.service.';
import { UntypedFormBuilder, Validators, UntypedFormGroup } from '@angular/forms';
import { EventService } from '../service/event.service';
import { Router } from '@angular/router';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ValidateAndNavigateEvent, BeforeSubmitErrorEvent, ProgressBarEvent, ChangeNavigationEvent, SkipToMainContentEvent } from '../service/events';
import { AFCpaInfo } from '../model/af.model';
import { Subscription } from 'rxjs';
import { DatePipe } from '@angular/common';
import { CustomValidatorsService } from '../service/custom-validators.service';

@Component({
    selector: 'app-cpa',
    templateUrl: './cpa.component.html',
    styleUrls: ['./cpa.component.scss'],
    standalone: false
})
export class CpaComponent extends BaseComponent implements OnInit, AfterViewInit, OnDestroy {

  cpaGroup: UntypedFormGroup;
  subcription: Subscription;
  cpaData: AFCpaInfo;
  date: any;
  signatureImg: string; // @Input wet-signature
  check: boolean = false;
  sigCheckValue: boolean = false;
  regexName: RegExp = /^[A-Za-z\u00C0-\u017F\-',. ]+$/;

  constructor(private userService: AuthService, private datepipe: DatePipe, private addressService: AddressService, private fb: UntypedFormBuilder, private eventBus: EventService,
    private _router: Router, private _snackBar: MatSnackBar, private customValidator: CustomValidatorsService) {
    super(eventBus, _router);
  }

  ngOnInit() {
    this.subscribeEvents();
    this.createGroup();
  }

  get submit() { return this.cpaGroup.get('submit') }
  get output() { return this.cpaGroup.get('output') }
  get cpaLegalRequirementsMet() { return this.cpaGroup.get('cpaLegalRequirementsMet') }
  get cpaRequirementsBgcCheckDate() { return this.cpaGroup.get('cpaRequirementsBgcCheckDate') }
  get cpaSignedName() { return this.cpaGroup.get('cpaSignedName') }
  get verifyId() { return this.cpaGroup.get('verifyId') }
  get bookmarkUrl() { return this.cpaGroup.get('bookmarkUrl') }


  subscribeEvents() {
    this.addressService.getCpaHistory().subscribe({
      next: (data: AFCpaInfo) => {
        this.cpaData = data;
        this.cpaGroup.patchValue(this.cpaData);
        this.date = this.cpaData.cpaRequirementsBgcCheckDate;
        this.signatureImg = this.output.value;
      },
      error: (error: Error) => {
        console.log(error);
      }
    });

    this.sub = this.eventBus.on(
      ValidateAndNavigateEvent,
      (event): void => {
        if (event.payload.currentLink === '/cpa') {
          this.bookmarkUrl.setValue("");
          if (event.payload.save) {
            this.submitCpaHistory((this.cpaGroup.value), event);
          } else if (event.payload.saveAndFinishLater) {
            this.bookmarkUrl.setValue('/addressHistory');
            this.submitCpaHistory((this.cpaGroup.value), event);
          } else if (this.validate()) {
            this.submitCpaHistory((this.cpaGroup.value), event);
          } else {
            this.eventBus.emit(new BeforeSubmitErrorEvent({}));
          }
        }
      }
    );

    this.eventBus.on(
      SkipToMainContentEvent,
      (event): void => {
        if (event.payload.text && event.payload.text == "/cpa") {
          let mainContentElement = document.querySelector("#main-content");//check if main-content id exists on page
          if (mainContentElement) {
            mainContentElement.scrollIntoView(true);
          }
        }
      }
    );
  }

  getUrl(sig: string) {
    this.signatureImg = sig; // Gets the base64 String from wet-signature @Output
    this.output.setValue(sig);
  }

  sigCheck(sigCheck: boolean) {
    this.sigCheckValue = sigCheck;
  }

  onFocusOutMainContent(event) {
    this.eventBus.emit(new SkipToMainContentEvent({ text: "FocusOff" }));
  }

  setDate() {
    if (this.cpaLegalRequirementsMet.value) {
      if (!this.date) {
        if (!this.cpaData.cpaRequirementsBgcCheckDate) {
          this.date = this.datepipe.transform(new Date(), 'MM/dd/yyyy HH:mm:ss');
        }
      }
    } else if (!this.cpaData.cpaRequirementsBgcCheckDate) {
      this.date = null;
    }
    
    this.cpaRequirementsBgcCheckDate.setValue(this.date);
  }

  legalOnBlur() {
    if (this.cpaLegalRequirementsMet.value) {
      this.check = false;
    } else {
      this.check = true;
    }
  }

  legalOnFocus() {
    this.check = false;
  }

  submitCpaHistory(cpaHistory, event) {
    this.addressService.submitCpaHistory(cpaHistory).subscribe({
      next: () => {
        if (event.payload.save) {
          this._snackBar.open("Saved successfully", "Close", {
            duration: 4000,
          });
        } else if (event.payload.saveAndFinishLater) {
          this._snackBar.open("Saved successfully", "Close", {
            duration: 4000,
          });
          this._router.navigate(['sessionTimedOut']);
        } else {
          this.eventBus.emit(new ChangeNavigationEvent({ id: event.payload.id }));
          this._router.navigateByUrl(event.payload.nextNavLink);
        }
      },
      error: (error: any) => console.log(error)
    });
  }

  validate() {
    this.submit.setValue(true);
    this.cpaGroup.markAllAsTouched();
    this.check = true;

    if (!this.output.value) {
      this.output.setErrors({ invalid: true });
    }

    return !this.cpaGroup.invalid;
  }

  ngAfterViewInit() {
    this.cpaGroup.valueChanges.subscribe(() => {
      setTimeout(() => {
        let progress = this.getPercentage(this.cpaGroup);
        this.eventBus.emit(new ProgressBarEvent({ id: progress }));
      }, 100);
    });

    this.cpaLegalRequirementsMet.valueChanges.subscribe(() => {
      this.setDate();
    });

    // retain navigation on page refresh
    let navlink = this.userService.getNavLinks().find(e => e.routerLink === "/cpa");
    if (navlink) {
      this.eventBus.emit(new ChangeNavigationEvent({ id: navlink.id }));
    }

  }

  getPercentage(container: UntypedFormGroup): number {
    let errorCount = 0;
    let totalCount = 0;

    for (let controlKey in container.controls) {

      if (container.controls.hasOwnProperty(controlKey)) {
        if (container.controls[controlKey].validator != null) {
          totalCount += container.controls[controlKey].validator.length;
        }
        if (container.controls[controlKey].errors) {
          errorCount += Object.keys(container.controls[controlKey].errors).length;
        }
      }
    }

    return ((totalCount - errorCount) / totalCount) * 100;
  }

  createGroup() {
    this.cpaGroup = this.fb.group({
      verifyId: [this.userService.getVerifyId()],
      cpaLegalRequirementsMet: [false, Validators.requiredTrue],
      output: ['', Validators.required],
      cpaSignedName: ['', [Validators.required, Validators.maxLength(250), Validators.pattern(this.regexName), this.customValidator.validateRequiredField]],
      cpaRequirementsBgcCheckDate: [''],
      submit: [false],
      bookmarkUrl: ['']
    });
  }

  ngOnDestroy(): void {
    this.sub.unsubscribe();
  }
}
